import {config} from "../config";
import {telemetry} from "../telemetry";
import * as secrets from "../secrets";
import * as error from "../error";

const fork = function (fnToFork) {

    var _callbackQ = [];

    var _returned = false;
    var _err = null;
    var _result = null;

    var _fireCallbacksIfReadyAndThereAreAny = function () {
        //Are we ready?
        if (!_returned) {
            return;
        }

        //Are there any?
        if (!_callbackQ.length) {
            return;
        }

        var callbackQ = _callbackQ;
        _callbackQ = [];
        return setImmediate(function () {
            return callbackQ.forEach(function (callback) {
                return callback(_err, _result);
            });
        });
    };

    var readyToJoin = function (err, result) {
        _returned = true;
        _err = err;
        _result = result;
        return _fireCallbacksIfReadyAndThereAreAny();
    };

    var join = function (callback) {
        _callbackQ.push(callback);
        return _fireCallbacksIfReadyAndThereAreAny();
    };

    setImmediate(fnToFork.bind(null, readyToJoin));
    return join;
};

import {RefreshToken, AccessToken} from "./auth0"

var _saveRefreshToken = function (refreshTokenStr) {
    if (!refreshTokenStr) {
        refreshTokenStr = null;
    }
    return secrets.set("refreshToken", refreshTokenStr);
};

var _saveRefreshTokenClientId = function (refreshTokenClientId) {
    if (!refreshTokenClientId) {
        refreshTokenClientId = null;
    }
    return secrets.set("refreshTokenClientId", refreshTokenClientId);
};

var _loadRefreshToken = function () {
    return secrets.get("refreshToken");
};

var _loadRefreshTokenClientId = function () {
    return secrets.get("refreshTokenClientId");
};

var _saveAccessTokenObj = function (accessTokenObj) {
    var clientId = null;
    var token = null;
    if (accessTokenObj && accessTokenObj.getTokenObject()) {
        clientId = accessTokenObj.clientId();
        token = JSON.stringify(accessTokenObj.getTokenObject());
    }

    return Promise.all([
        secrets.set("accessTokenClientId", clientId),
        secrets.set("accessToken", token)
    ]);
};

var _loadAccessTokenObj = function () {
    var clientId = secrets.get("accessTokenClientId");
    var token = secrets.get("accessToken");
    try {
        token = JSON.parse(token);
    }
    catch (e) {
        token = null;
    }
    if (!clientId || !token) {
        return null;
    }
    return new AccessToken(clientId, token);
};

/**
 *
 * Manages a refresh token, it saving and loading and the associated Access tokens and their renewals
 * Also manages continual refreshing of the access token
 * @param refreshToken string
 * @param options
 * @constructor
 */
export class ActiveAuth {

    private joinWithReply;
    private accessTokenObj;

    constructor(private refreshTokenObj, private options?) {
        var This = this;

        This.joinWithReply = null;
        This.options = options || {};


        [
            {
                key: "clientId",
                defaultValue: config.auth0UserClientId
            },
            {
                key: "refreshTokenClientId",
                defaultValue: config.auth0UserClientId
            },
            "secondsOkToUseTokenBeforeExpiry",
            "secondsUntilTokenRefresh",
            {
                key: "previousAccessTokenObj",
                defaultValue: null
            }
        ].forEach(function (prop: any) {
            if (typeof prop === "string" || prop instanceof String) {
                prop = {
                    key: prop
                };
            }
            if (!(prop.key in This.options)) {
                var val;
                if ("defaultValue" in prop) {
                    val = prop.defaultValue;
                }
                else {
                    val = config[prop.key];
                }
                This.options[prop.key] = val;
            }
        });
        This.accessTokenObj = This.options.previousAccessTokenObj;
        if (This.accessTokenObj && !options.disableAutoStart) {
            //Validate the access token from the constructor, it is outdated, refresh it.
            This.getAccessToken(function () {});
        }
    }


    /**
     * Generate a OfflineAuth object from the saved refresh token
     * @returns {OfflineAuth}
     */
    static fromSavedData(options?) {
        var refreshTokenObj = new RefreshToken(_loadRefreshToken());
        var accessTokenObj = _loadAccessTokenObj();
        options = options || {};
        const refreshTokenClientId = _loadRefreshTokenClientId();
        if (refreshTokenClientId) {
            options.refreshTokenClientId =refreshTokenClientId;
        }
        options.previousAccessTokenObj = accessTokenObj;
        return new ActiveAuth(refreshTokenObj, options);
    }


    /**
     * Generate a OfflineAuth object from a refreshToken
     * @param refreshToken
     * @returns {exports.OfflineAuth}
     */
    static fromRefreshToken(refreshToken, options?) {
        var refreshTokenObj = new RefreshToken(refreshToken);
        return new ActiveAuth(refreshTokenObj, options);
    }


    /**
     * Makes it so that the same token will be used the next time auth.OfflineAuth.fromSavedData is called.
     */
    save() {
        var This = this;
        return Promise.all([
            _saveRefreshToken(This.refreshToken().token()),
            _saveAccessTokenObj(This.accessTokenObj),
            _saveRefreshTokenClientId(This.options.refreshTokenClientId)
        ]);
    }

    /**
     * Gets the refreshTokenObj
     * @returns {auth.RefreshToken|*}
     */
    refreshToken() {
        return this.refreshTokenObj;
    };

    //Generate a NEW token
    _generateNewAccessTokenObj(callback) {
        var This = this;
        var refreshTokenObj = This.refreshTokenObj;
        var refreshTokenStr = refreshTokenObj.token();
        if (!refreshTokenStr || !refreshTokenStr.length) {
            return setImmediate(callback.bind(null, new error.client.BadRequest("Missing Refresh Token")));
        }
        return refreshTokenObj.generateIdToken(
            This.options.refreshTokenClientId,
            This.options.clientId,
            function (err, accessTokenObj) {
                if (err) {
                    return setImmediate(callback.bind(null, err));
                }

                var timeLeft = accessTokenObj.timeLeft();
                if (timeLeft === null) {
                    return setImmediate(
                        callback.bind(null, new error.client.Unauthorized("Invalid token was generated"))
                    );
                }

                if (timeLeft < This.options.secondsOkToUseTokenBeforeExpiry) {
                    return setImmediate(
                        callback.bind(null, new error.client.Unauthorized("Token already expired"))
                    );
                }

                return setImmediate(callback.bind(null, null, accessTokenObj));
            }
        );

    };

    getAccessToken(callback) {

        var This = this;

        var shouldRefresh;
        var returnCurrent;
        var options = This.options;

        if (This.accessTokenObj && This.accessTokenObj.timeLeft() >= options.secondsOkToUseTokenBeforeExpiry) {
            returnCurrent = true;

            //When returnCurrent = true, we need to decide if or if not to fire the refresh
            if (This.accessTokenObj.timeLeft() >= options.secondsUntilTokenRefresh) {
                shouldRefresh = false;
            }
            else {
                shouldRefresh = true;
            }
        }
        else {
            shouldRefresh = true;
            returnCurrent = false;
        }

        if (shouldRefresh) {
            //Is there a refresh in progress, if not create it.
            if (!This.joinWithReply) {
                This.joinWithReply = fork(This._generateNewAccessTokenObj.bind(This));
                This.joinWithReply(function (err, accessTokenObj) {
                    This.joinWithReply = null;
                    if (err) {
                        telemetry.error(err);
                        return;
                    }
                    This.accessTokenObj = accessTokenObj;
                });
            }
        }

        //If returnCurrent = false, joinWithReply should be generated above already
        if (!returnCurrent) {
            return This.joinWithReply(function (err) {
                if (err) {
                    return setImmediate(callback.bind(null, err));
                }
                // Call getAccessToken to do the should refreshCheck with this new token.
                // Otherwise we could have just passed callback to _joinWithReply
                return This.getAccessToken(callback);
            });
        }
        return setImmediate(callback.bind(null, null, This.accessTokenObj));
    }
}


export function activeAuthKey(activeAuth: ActiveAuth) {
    if (!activeAuth) {
        return null;
    }

    return activeAuth.refreshToken() && activeAuth.refreshToken().token() || null;
};

export function activeAuthIsNull(activeAuth: ActiveAuth) {
    return activeAuthKey(activeAuth) === null;
};

export class ActiveAuthMissingError extends Error {
    constructor(message?: string) {
        super(message)
        this.name = 'ActiveAuthMissingError';
    }
}